import CallToActionOutlinedIcon from '@mui/icons-material/CallToActionOutlined'
import FlipToFrontIcon from '@mui/icons-material/FlipToFront'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined'
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined'
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import * as Sentry from '@sentry/browser'
import { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useResetAiChatConversationMutation } from '../../../graphql/mutations/aiChat/__generated__/resetAiChatConversationMutation.generated'
import { useAiChatDefaultAgentWelcomeMessageQuery } from '../../../graphql/queries/aiChat/__generated__/defaultAgentWelcomeMessageQuery.generated'
import { useMyActiveAiChatConversationLazyQuery } from '../../../graphql/queries/aiChat/__generated__/myActiveAiChatConversationQuery.generated'
import type { ChatDrawerMode, ChatDrawerPosition, ChatMessageRole } from '../../../stores/chatStore'
import { useChatStore } from '../../../stores/chatStore'
import colors from '../../../theme/colors'
import { useAiChatChannel, useCurrentViewContext, useProfile } from '../../../utils/hooks'
import UntypedDrawer from '../../atoms/Drawer'
import ConfirmationDialog from '../../molecules/ConfirmationDialog'
import AiChatInput from './AiChatInput'
import AiChatMessageList from './AiChatMessageList'

// Drawer.jsx ist ein untyptes JS-Atom (nur PropTypes) — TS leitet daraus zu
// enge Typen her (z.B. `variant?: undefined` aus dem Default-Wert). Statt das
// gemeinsam genutzte Atom für diesen einen Verbraucher anzufassen, wird der
// tatsächliche (PropTypes-)Vertrag hier lokal nachgebildet.
interface DrawerProps {
  open: boolean
  setOpen: (open: boolean) => void
  variant?: 'temporary' | 'permanent' | 'persistent'
  drawerWidth?: number
  drawerHeight?: number
  withBottomBar?: boolean
  bottomBarComponent?: ReactNode
  toggleButton?: ReactNode
  anchor?: ChatDrawerPosition
  resizable?: boolean
  onResize?: (size: number) => void
  contentPadding?: number
  bottomBarPadding?: number
  closeButtonSx?: Record<string, unknown>
  children: ReactNode
}
const Drawer = UntypedDrawer as unknown as FC<DrawerProps>

// Weiße Toggle-Buttons auf dem petrolfarbenen Header — Selected-Zustand
// bleibt über den halbtransparenten weißen Hintergrund unterscheidbar.
const headerToggleGroupSx = {
  '& .MuiToggleButton-root': {
    color: 'rgba(255, 255, 255, 0.72)',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
    '&.Mui-selected': {
      color: colors.white,
      backgroundColor: 'rgba(255, 255, 255, 0.16)',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.24)' }
    }
  }
}

// Agenten-Select auf dem petrolfarbenen Header — gleiche halbtransparente
// weiße Optik wie die Toggle-Buttons (headerToggleGroupSx), nur als Outlined
// Select statt ToggleButtonGroup.
const headerSelectSx = {
  color: colors.white,
  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.4)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.72)' },
  '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.72)' },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
  '.MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.72)' }
}

/**
 * AiChat-Root: hier läuft die einzige useAiChatChannel()-Subscription für die
 * gesamte App (siehe Task-7-Brief). Die Komponente wird unabhängig von
 * isOpen dauerhaft gemountet (nur gated durch me?.aiAvailable in
 * ContextWrapper), damit die Subscription auch bestehen bleibt, während der
 * Drawer eingeklappt ist.
 *
 * Layout: Position (links/rechts/unten), Größe und Modus (Overlay vs. Push)
 * sind User-Präferenzen im chatStore (persistiert in localStorage). Im
 * Push-Modus weicht das restliche Layout dem Drawer aus — umgesetzt im
 * ContextWrapper über Margins/Offsets an Main, TopBar und SideBar.
 */
const AiChatDrawer: FC = () => {
  const { t } = useTranslation('ai')

  const isOpen = useChatStore(state => state.isOpen)
  const closeChat = useChatStore(state => state.close)
  const conversationId = useChatStore(state => state.conversationId)
  const messages = useChatStore(state => state.messages)
  const status = useChatStore(state => state.status)
  const addMessage = useChatStore(state => state.addMessage)
  const hydrated = useChatStore(state => state.hydrated)
  const setHydrated = useChatStore(state => state.setHydrated)
  const setConversationId = useChatStore(state => state.setConversationId)
  const resetStore = useChatStore(state => state.reset)
  const selectedAgentId = useChatStore(state => state.selectedAgentId)
  const setSelectedAgentId = useChatStore(state => state.setSelectedAgentId)
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [resetFailed, setResetFailed] = useState(false)

  const { me } = useProfile()
  // Nur chat-fähige Agenten anzeigen — Agenten ohne chatEnabled sind für
  // andere Oberflächen (z.B. AiLegalAdviceForm) gedacht.
  const chatAgents = (me?.aiAgents ?? []).filter(
    (agent: { chatEnabled?: boolean }) => agent.chatEnabled
  )

  // Willkommensnachricht für den leeren Chat: bei gewähltem Agenten dessen
  // (in Hermine gepflegte) Nachricht, sonst die des Standard-Agenten — die
  // Query läuft nur, wenn sie gebraucht wird. Liefert der Agent keine,
  // bleibt der statische Hinweis (kein Fallback auf den Standard-Agenten:
  // dessen Begrüßung wäre die falsche Absender-Stimme).
  const { data: defaultWelcomeData } = useAiChatDefaultAgentWelcomeMessageQuery({
    skip: !isOpen || Boolean(selectedAgentId)
  })
  const welcomeMessage = selectedAgentId
    ? (
        chatAgents.find((agent: { id: string }) => agent.id === selectedAgentId) as
          | { welcomeMessage?: string | null }
          | undefined
      )?.welcomeMessage
    : defaultWelcomeData?.aiChatDefaultAgentWelcomeMessage

  // Heilt eine stale persistierte Agentenauswahl: Wurde dem User der gewählte
  // Agent entzogen (oder dessen hermine_agent_id geleert), ist er nicht mehr
  // in chatAgents enthalten und würde beim nächsten Senden ai_chat.invalidAgent
  // auslösen (AiChatInput gated das zusätzlich, dies hier heilt die
  // persistierte Auswahl selbst). Nur ohne laufende Konversation zurücksetzen
  // — deren Agent wurde per Hydration gesetzt (siehe fetchActiveConversation
  // .onCompleted) und darf nicht überschrieben werden. Erst feuern, wenn das
  // Profil tatsächlich geladen ist: me startet als {} (siehe
  // ProfileContextProvider), aiAgents ist dann noch undefined — das darf
  // nicht mit einer echten leeren Liste verwechselt werden.
  useEffect(() => {
    if (conversationId) return
    if (!selectedAgentId) return
    if (!Array.isArray(me?.aiAgents)) return

    const stillValid = chatAgents.some((agent: { id: string }) => agent.id === selectedAgentId)
    if (!stillValid) setSelectedAgentId(null)
  }, [conversationId, selectedAgentId, me, setSelectedAgentId]) // eslint-disable-line react-hooks/exhaustive-deps

  const [resetConversation, { loading: resetLoading }] = useResetAiChatConversationMutation({
    onCompleted: () => {
      // Server hat archiviert — erst jetzt den Client-State leeren, damit
      // UI und Server nie auseinanderlaufen. reset() behält layout und
      // hält hydrated auf true (keine erneute Hydration).
      resetStore()
      setResetDialogOpen(false)
    },
    onError: error => {
      // Store bewusst NICHT leeren — Dialog bleibt offen und zeigt den
      // Fehler, der Verlauf bleibt sichtbar.
      Sentry.captureException(error)
      setResetFailed(true)
    }
  })

  const viewContext = useCurrentViewContext()
  const [rateLimited, setRateLimited] = useState(false)

  const layout = useChatStore(state => state.layout)
  const setLayout = useChatStore(state => state.setLayout)

  // Der Drawer-Inhalt darf nie horizontal scrollen: die Kopfzeile (Titel +
  // Agenten-Dropdown + Dock-/Mode-Toggles + Reset) definiert eine
  // Mindestbreite. Die Controls werden in ihrer natürlichen Breite gemessen
  // (width: max-content, shrinkt nie) — dazu kommen die Header-Paddings, der
  // Gap zum Titel und eine Mindestbreite für den Titel selbst (längere Titel
  // truncaten per Ellipsis, statt den Drawer unbegrenzt aufzuziehen). Der
  // Abstand zum absolut positionierten Schließen-Button steckt bereits als
  // mr: 8 im Reset-Button innerhalb der Controls.
  const HEADER_PADDING_X = 32
  const HEADER_GAP = 8
  const TITLE_MIN_WIDTH = 120

  const headerControlsRef = useRef<HTMLDivElement>(null)
  const [minDrawerWidth, setMinDrawerWidth] = useState(0)

  useLayoutEffect(() => {
    if (!isOpen) return
    const controls = headerControlsRef.current
    if (!controls) return
    setMinDrawerWidth(controls.offsetWidth + HEADER_PADDING_X + HEADER_GAP + TITLE_MIN_WIDTH)
    // selectedAgentId ändert die Breite des Select-Labels, chatAgents die
    // Sichtbarkeit des Dropdowns.
  }, [isOpen, chatAgents.length, selectedAgentId])

  const effectiveDrawerWidth = Math.max(layout.width, minDrawerWidth)

  const handleResize = (size: number) => {
    setLayout(
      layout.position === 'bottom' ? { height: size } : { width: Math.max(size, minDrawerWidth) }
    )
  }

  const handlePositionChange = (_event: unknown, position: ChatDrawerPosition | null) => {
    if (position) setLayout({ position })
  }

  const handleModeChange = (_event: unknown, mode: ChatDrawerMode | null) => {
    if (mode) setLayout({ mode })
  }

  useAiChatChannel()

  // Serverseitige Hydration: beim ersten Öffnen des Drawers (pro Session)
  // die zuletzt aktive Konversation des Users laden — so überlebt der
  // Verlauf Reloads und Gerätewechsel. Ersetzt die frühere Rehydrierung
  // per bekannter conversationId (die nach einem Reload nie gesetzt war).
  // Nach setConversationId übernimmt useAiChatChannel (Subscription +
  // connected-Resync); addMessage dedupliziert per Upsert, daher sind die
  // beiden Ladepfade konfliktfrei.
  const [fetchActiveConversation] = useMyActiveAiChatConversationLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: data => {
      // Guard gegen veraltete Antworten: Ist der Store während des
      // Hydration-Roundtrips schon weitergezogen (User hat gesendet →
      // neue conversationId, oder Reset → hydrated true), darf die
      // Antwort weder Messages noch conversationId mehr anfassen.
      const { hydrated: alreadyHydrated, conversationId: currentId } = useChatStore.getState()
      if (alreadyHydrated || currentId) {
        setHydrated()
        return
      }

      const conversation = data.myActiveAiChatConversation
      conversation?.messages?.forEach(message => {
        if (!message?.id) return
        addMessage({
          id: message.id,
          role: (message.role as ChatMessageRole) ?? 'assistant',
          content: message.content ?? '',
          status: message.status,
          createdAt: message.createdAt ?? undefined,
          uploads: (message.uploads ?? []).map(upload => ({
            filename: upload.attachmentFilename ?? '',
            url: upload.attachmentUrl
          }))
        })
      })
      if (conversation?.id) {
        setConversationId(conversation.id)
        // Gesperrtes Dropdown soll den tatsächlichen Agenten der geladenen
        // Konversation zeigen, nicht die zuletzt gewählte Präferenz.
        setSelectedAgentId(conversation.agent?.id ?? null)
      }
      setHydrated()
    },
    onError: error => {
      // Fehlgeschlagene Hydration ist kein harter Fehler: Chat startet leer,
      // kein Retry-Loop — aber hydrated setzen, sonst feuert der Effect bei
      // jedem Öffnen erneut.
      Sentry.captureException(error)
      setHydrated()
    }
  })

  // Ref verhindert Doppel-Requests (z. B. StrictMode-Doppel-Mount im Dev),
  // solange die Antwort noch aussteht.
  const hydrationRequestedRef = useRef(false)
  useEffect(() => {
    if (!isOpen || hydrated || conversationId) return
    if (hydrationRequestedRef.current) return

    hydrationRequestedRef.current = true
    fetchActiveConversation()
  }, [isOpen, hydrated, conversationId, fetchActiveConversation])

  return (
    <Drawer
      open={isOpen}
      setOpen={(open: boolean) => {
        if (!open) closeChat()
      }}
      variant="persistent"
      drawerWidth={effectiveDrawerWidth}
      drawerHeight={layout.height}
      anchor={layout.position}
      resizable
      onResize={handleResize}
      withBottomBar
      bottomBarComponent={<AiChatInput onRateLimitedChange={setRateLimited} />}
      toggleButton={null}
      // Padding sitzt bewusst innerhalb von Header und MessageList, damit
      // die Liste (inkl. Scrollbar) bis an die Drawer-Kante reicht.
      contentPadding={0}
      bottomBarPadding={2}
      // Schließen-Button in den petrolfarbenen Header einpassen: weiß statt
      // secondary und vertikal auf die 64px-Headerzeile zentriert.
      closeButtonSx={{
        top: '16px',
        right: '16px',
        '& .MuiIconButton-root': {
          color: colors.white,
          backgroundColor: 'transparent',
          '&:hover': { color: colors.white, backgroundColor: 'rgba(255, 255, 255, 0.16)' },
          '&:active': { color: colors.white, backgroundColor: 'rgba(255, 255, 255, 0.24)' }
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            minHeight: 64,
            backgroundColor: colors.petrol,
            color: colors.white
          }}
        >
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
            {viewContext.title || t('chat.title')}
          </Typography>
          {/* Controls in natürlicher Breite (max-content, shrinkt nie) — die
              Box ist die Messbasis für die Mindestbreite des Drawers. */}
          <Box
            ref={headerControlsRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              width: 'max-content'
            }}
          >
            {chatAgents.length > 0 && (
              <Tooltip title={conversationId ? t('chat.agentLockedHint') : ''}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={selectedAgentId ?? ''}
                    onChange={event => setSelectedAgentId(event.target.value || null)}
                    disabled={Boolean(conversationId)}
                    displayEmpty
                    aria-label={t('chat.agentLabel')}
                    sx={headerSelectSx}
                  >
                    <MenuItem value="">{t('chat.agentDefault')}</MenuItem>
                    {chatAgents.map((agent: { id: string; name?: string | null }) => (
                      <MenuItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={layout.position}
              onChange={handlePositionChange}
              sx={headerToggleGroupSx}
            >
              <ToggleButton value="left" aria-label={t('chat.dockLeft')}>
                <Tooltip title={t('chat.dockLeft')}>
                  <ViewSidebarOutlinedIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="bottom" aria-label={t('chat.dockBottom')}>
                <Tooltip title={t('chat.dockBottom')}>
                  <CallToActionOutlinedIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="right" aria-label={t('chat.dockRight')}>
                <Tooltip title={t('chat.dockRight')}>
                  <ViewSidebarOutlinedIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={layout.mode}
              onChange={handleModeChange}
              sx={headerToggleGroupSx}
            >
              <ToggleButton value="overlay" aria-label={t('chat.modeOverlay')}>
                <Tooltip title={t('chat.modeOverlay')}>
                  <FlipToFrontIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="push" aria-label={t('chat.modePush')}>
                <Tooltip title={t('chat.modePush')}>
                  <VerticalSplitOutlinedIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title={t('chat.reset')}>
              {/* span: Tooltip braucht bei disabled Buttons einen Wrapper.
                mr: 8 = Abstand zum absolut positionierten Schließen-Button
                des Drawers (wandert vom Mode-ToggleButtonGroup hierher). */}
              <Box component="span" sx={{ mr: 8 }}>
                <IconButton
                  size="small"
                  aria-label={t('chat.reset')}
                  sx={{
                    color: colors.white,
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                    '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.4)' }
                  }}
                  disabled={status === 'waiting'}
                  onClick={() => {
                    setResetFailed(false)
                    setResetDialogOpen(true)
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <AiChatMessageList
          messages={messages}
          status={status}
          rateLimited={rateLimited}
          welcomeMessage={welcomeMessage}
        />
      </Box>
      <ConfirmationDialog
        open={resetDialogOpen}
        title={t('chat.resetConfirmTitle')}
        content={resetFailed ? t('chat.resetError') : t('chat.resetConfirmText')}
        cancelButtonText={t('chat.resetCancel')}
        confirmButtonText={t('chat.resetConfirm')}
        handleCancel={() => setResetDialogOpen(false)}
        handleConfirm={() => {
          if (!resetLoading) resetConversation()
        }}
        maxWidth="xs"
      />
    </Drawer>
  )
}

export default AiChatDrawer
